import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, Microscope, Award, FileText, Search, ArrowRight, ShieldCheck, MapPin, BarChart3, FlaskConical, Activity, Leaf, Factory } from 'lucide-react';
import { EnhancedScrollReveal } from '../common/EnhancedScrollReveal';
import { Button } from '../ui/Button';
import { Badge } from '../ui/Badge';
export function QualityShowcase() {
  const [activeTab, setActiveTab] = useState<'sourcing' | 'lab' | 'standards'>('sourcing');
  const metrics = {
    tests: '1,250+',
    approvalRate: '99.8%',
    standards: 'FEDIAF',
    frequency: 'Cada Lote'
  };
  return <section className="py-24 lg:py-32 bg-charcoal-900 text-white overflow-hidden relative" data-id="element-3107">
      {/* Background Grid & Noise */}
      <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-20" data-id="element-3108" />
      <div className="absolute inset-0 bg-gradient-to-b from-charcoal-900 via-transparent to-charcoal-900" data-id="element-3109" />

      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-emerald-900/20 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/3 pointer-events-none" data-id="element-3110" />
      <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-blue-900/10 rounded-full blur-[100px] translate-y-1/3 -translate-x-1/4 pointer-events-none" data-id="element-3111" />

      <div className="container-custom max-w-7xl mx-auto relative z-10" data-id="element-3112">
        <div className="flex flex-col lg:flex-row gap-16 items-start" data-id="element-3113">
          {/* Left: Narrative & Navigation */}
          <div className="lg:w-1/3 sticky top-24" data-id="element-3114">
            <EnhancedScrollReveal data-id="element-3115">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-6" data-id="element-3116">
                <ShieldCheck className="w-4 h-4" data-id="element-3117" />
                <span data-id="element-3118">Transparencia Radical</span>
              </div>

              <h2 className="text-4xl md:text-5xl font-serif font-bold text-white mb-6 leading-tight" data-id="element-3119">
                Obsesionados con la{' '}
                <span className="text-emerald-400" data-id="element-3120">Excelencia</span>
              </h2>

              <p className="text-lg text-gray-400 leading-relaxed mb-8" data-id="element-3121">
                En una industria a menudo opaca, elegimos la claridad total. No
                solo cumplimos los estándares; los redefinimos con un control de
                calidad que va desde el suelo de la granja hasta tu despensa.
              </p>

              {/* Key Metrics Summary */}
              <div className="grid grid-cols-2 gap-4 mb-10 border-y border-white/10 py-6" data-id="element-3122">
                <div data-id="element-3123">
                  <div className="text-3xl font-bold text-white mb-1" data-id="element-3124">
                    {metrics.tests}
                  </div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-3125">
                    Tests Anuales
                  </div>
                </div>
                <div data-id="element-3126">
                  <div className="text-3xl font-bold text-emerald-400 mb-1" data-id="element-3127">
                    {metrics.approvalRate}
                  </div>
                  <div className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-3128">
                    Tasa Aprobación
                  </div>
                </div>
              </div>

              <div className="space-y-3" data-id="element-3129">
                {[{
                id: 'sourcing',
                label: 'Origen y Trazabilidad',
                desc: 'Ingredientes Km 0',
                icon: MapPin
              }, {
                id: 'lab',
                label: 'Análisis de Laboratorio',
                desc: 'Seguridad Certificada',
                icon: Microscope
              }, {
                id: 'standards',
                label: 'Certificaciones',
                desc: 'Auditorías Externas',
                icon: Award
              }].map(item => <button key={item.id} onClick={() => setActiveTab(item.id as any)} className={`w-full flex items-center gap-4 p-4 rounded-xl transition-all duration-300 border text-left group ${activeTab === item.id ? 'bg-white/10 border-emerald-500/50 shadow-lg shadow-emerald-900/20' : 'bg-transparent border-white/5 hover:bg-white/5'}`} data-id="element-3130">
                    <div className={`w-12 h-12 rounded-lg flex items-center justify-center transition-colors ${activeTab === item.id ? 'bg-emerald-500 text-white' : 'bg-white/5 text-gray-400 group-hover:text-white'}`} data-id="element-3131">
                      <item.icon className="w-6 h-6" data-id="element-3132" />
                    </div>
                    <div className="flex-1" data-id="element-3133">
                      <div className={`font-bold text-lg ${activeTab === item.id ? 'text-white' : 'text-gray-400 group-hover:text-white'}`} data-id="element-3134">
                        {item.label}
                      </div>
                      <div className="text-sm text-gray-500 group-hover:text-gray-400" data-id="element-3135">
                        {item.desc}
                      </div>
                    </div>
                    <ArrowRight className={`w-5 h-5 transition-all ${activeTab === item.id ? 'opacity-100 translate-x-0 text-emerald-400' : 'opacity-0 -translate-x-4'}`} data-id="element-3136" />
                  </button>)}
              </div>
            </EnhancedScrollReveal>
          </div>

          {/* Right: Interactive Dashboard */}
          <div className="lg:w-2/3 w-full" data-id="element-3137">
            <EnhancedScrollReveal direction="left" delay={0.2} data-id="element-3138">
              <div className="bg-charcoal-800 rounded-[2.5rem] border border-white/10 p-1 min-h-[650px] relative overflow-hidden shadow-2xl backdrop-blur-sm" data-id="element-3139">
                <div className="h-full bg-charcoal-900/50 rounded-[2.3rem] p-8 md:p-12 overflow-hidden relative" data-id="element-3140">
                  <AnimatePresence mode="wait" data-id="element-3141">
                    {/* TAB: SOURCING */}
                    {activeTab === 'sourcing' && <motion.div key="sourcing" initial={{
                    opacity: 0,
                    x: 20
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: -20
                  }} transition={{
                    duration: 0.3
                  }} className="h-full flex flex-col" data-id="element-3142">
                        <div className="flex justify-between items-start mb-8" data-id="element-3143">
                          <div data-id="element-3144">
                            <Badge variant="success" className="mb-3 bg-emerald-500/20 text-emerald-400 border-emerald-500/30" data-id="element-3145">
                              Filosofía Km 0
                            </Badge>
                            <h3 className="text-3xl font-bold text-white mb-2" data-id="element-3146">
                              Mapa de Trazabilidad
                            </h3>
                            <p className="text-gray-400" data-id="element-3147">
                              Conocemos a cada proveedor por su nombre.
                              Priorizamos ingredientes locales para garantizar
                              frescura y reducir la huella de carbono.
                            </p>
                          </div>
                        </div>

                        {/* Map Visualization */}
                        <div className="flex-1 bg-charcoal-950/50 rounded-2xl border border-white/5 relative p-6 overflow-hidden group" data-id="element-3148">
                          <div className="absolute inset-0 opacity-20 bg-[url('https://upload.wikimedia.org/wikipedia/commons/thumb/b/b3/Spain_location_map.svg/1024px-Spain_location_map.svg.png')] bg-contain bg-center bg-no-repeat grayscale invert" data-id="element-3149" />

                          {/* Hotspots */}
                          {[{
                        top: '30%',
                        left: '35%',
                        title: 'Pollo de Corral',
                        loc: 'Galicia',
                        type: 'Carne Fresca'
                      }, {
                        top: '65%',
                        left: '75%',
                        title: 'Hortalizas',
                        loc: 'Murcia',
                        type: 'Vegetales'
                      }, {
                        top: '25%',
                        left: '70%',
                        title: 'Pato',
                        loc: 'Pirineos',
                        type: 'Carne Fresca'
                      }, {
                        top: '80%',
                        left: '40%',
                        title: 'Pescado Azul',
                        loc: 'Andalucía',
                        type: 'Pescado'
                      }].map((spot, i) => <div key={i} className="absolute group/spot cursor-pointer" style={{
                        top: spot.top,
                        left: spot.left
                      }} data-id="element-3150">
                              <div className="relative" data-id="element-3151">
                                <div className="w-4 h-4 bg-emerald-500 rounded-full animate-pulse shadow-[0_0_15px_rgba(16,185,129,0.5)]" data-id="element-3152" />
                                <div className="w-4 h-4 bg-emerald-500 rounded-full absolute inset-0 animate-ping opacity-75" data-id="element-3153" />
                              </div>

                              {/* Tooltip */}
                              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-3 w-48 bg-white text-charcoal-900 p-4 rounded-xl shadow-xl opacity-0 group-hover/spot:opacity-100 transition-all duration-300 pointer-events-none z-20 scale-95 group-hover/spot:scale-100 origin-bottom" data-id="element-3154">
                                <div className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider mb-1" data-id="element-3155">
                                  {spot.type}
                                </div>
                                <div className="font-bold text-lg leading-tight mb-1" data-id="element-3156">
                                  {spot.title}
                                </div>
                                <div className="text-xs text-gray-500 flex items-center gap-1" data-id="element-3157">
                                  <MapPin className="w-3 h-3" data-id="element-3158" /> {spot.loc}
                                </div>
                                <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-3 h-3 bg-white rotate-45" data-id="element-3159" />
                              </div>
                            </div>)}
                        </div>

                        <div className="grid grid-cols-3 gap-4 mt-6" data-id="element-3160">
                          <div className="bg-white/5 p-4 rounded-xl border border-white/5 text-center" data-id="element-3161">
                            <div className="text-2xl font-bold text-white mb-1" data-id="element-3162">
                              100%
                            </div>
                            <div className="text-xs text-gray-400" data-id="element-3163">
                              Nacional
                            </div>
                          </div>
                          <div className="bg-white/5 p-4 rounded-xl border border-white/5 text-center" data-id="element-3164">
                            <div className="text-2xl font-bold text-white mb-1" data-id="element-3165">
                              24h
                            </div>
                            <div className="text-xs text-gray-400" data-id="element-3166">
                              Del campo a fábrica
                            </div>
                          </div>
                          <div className="bg-white/5 p-4 rounded-xl border border-white/5 text-center" data-id="element-3167">
                            <div className="text-2xl font-bold text-white mb-1" data-id="element-3168">
                              Bio
                            </div>
                            <div className="text-xs text-gray-400" data-id="element-3169">
                              Certificado
                            </div>
                          </div>
                        </div>
                      </motion.div>}

                    {/* TAB: LAB */}
                    {activeTab === 'lab' && <motion.div key="lab" initial={{
                    opacity: 0,
                    x: 20
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: -20
                  }} transition={{
                    duration: 0.3
                  }} className="h-full flex flex-col" data-id="element-3170">
                        <div className="flex justify-between items-start mb-8" data-id="element-3171">
                          <div data-id="element-3172">
                            <Badge variant="primary" className="mb-3 bg-blue-500/20 text-blue-400 border-blue-500/30" data-id="element-3173">
                              Lote #8492-AX
                            </Badge>
                            <h3 className="text-3xl font-bold text-white mb-2" data-id="element-3174">
                              Certificado de Análisis
                            </h3>
                            <p className="text-gray-400" data-id="element-3175">
                              Cada lote es retenido hasta superar un panel
                              completo de seguridad y nutrición.
                            </p>
                          </div>
                          <Button size="sm" variant="outline" className="border-white/20 text-white hover:bg-white/10 hidden sm:flex" data-id="element-3176">
                            <FileText className="w-4 h-4 mr-2" data-id="element-3177" />
                            Ver Informe PDF
                          </Button>
                        </div>

                        <div className="bg-white rounded-2xl overflow-hidden text-charcoal-900 shadow-lg flex-1 flex flex-col" data-id="element-3178">
                          <div className="bg-gray-50 px-6 py-3 border-b border-gray-100 flex justify-between items-center" data-id="element-3179">
                            <span className="text-xs font-bold text-gray-500 uppercase" data-id="element-3180">
                              Parámetro
                            </span>
                            <div className="flex gap-8 text-xs font-bold text-gray-500 uppercase" data-id="element-3181">
                              <span className="w-20 text-right" data-id="element-3182">Estándar</span>
                              <span className="w-20 text-right" data-id="element-3183">Resultado</span>
                              <span className="w-8" data-id="element-3184"></span>
                            </div>
                          </div>

                          <div className="divide-y divide-gray-100 overflow-y-auto custom-scrollbar" data-id="element-3185">
                            {[{
                          name: 'Proteína Bruta',
                          std: '> 28.0%',
                          res: '28.4%',
                          status: 'pass'
                        }, {
                          name: 'Grasa Cruda',
                          std: '> 15.0%',
                          res: '15.2%',
                          status: 'pass'
                        }, {
                          name: 'Humedad',
                          std: '< 10.0%',
                          res: '8.5%',
                          status: 'pass'
                        }, {
                          name: 'Salmonella',
                          std: 'Negativo',
                          res: 'Negativo',
                          status: 'pass'
                        }, {
                          name: 'E. Coli',
                          std: 'Negativo',
                          res: 'Negativo',
                          status: 'pass'
                        }, {
                          name: 'Listeria',
                          std: 'Negativo',
                          res: 'Negativo',
                          status: 'pass'
                        }, {
                          name: 'Metales Pesados',
                          std: '< 0.1ppm',
                          res: 'ND',
                          status: 'pass'
                        }, {
                          name: 'Micotoxinas',
                          std: '< 5ppb',
                          res: 'ND',
                          status: 'pass'
                        }].map((row, i) => <div key={i} className="px-6 py-4 flex justify-between items-center hover:bg-gray-50 transition-colors" data-id="element-3186">
                                <span className="font-medium text-sm" data-id="element-3187">
                                  {row.name}
                                </span>
                                <div className="flex gap-8 items-center" data-id="element-3188">
                                  <span className="w-20 text-right text-sm text-gray-400 font-mono" data-id="element-3189">
                                    {row.std}
                                  </span>
                                  <span className="w-20 text-right text-sm font-bold text-charcoal-900 font-mono" data-id="element-3190">
                                    {row.res}
                                  </span>
                                  <div className="w-8 flex justify-end" data-id="element-3191">
                                    <CheckCircle2 className="w-5 h-5 text-emerald-500" data-id="element-3192" />
                                  </div>
                                </div>
                              </div>)}
                          </div>

                          <div className="bg-emerald-50 p-4 text-center border-t border-emerald-100" data-id="element-3193">
                            <p className="text-emerald-800 text-sm font-bold flex items-center justify-center gap-2" data-id="element-3194">
                              <ShieldCheck className="w-4 h-4" data-id="element-3195" />
                              Lote Aprobado para Distribución
                            </p>
                          </div>
                        </div>
                      </motion.div>}

                    {/* TAB: STANDARDS */}
                    {activeTab === 'standards' && <motion.div key="standards" initial={{
                    opacity: 0,
                    x: 20
                  }} animate={{
                    opacity: 1,
                    x: 0
                  }} exit={{
                    opacity: 0,
                    x: -20
                  }} transition={{
                    duration: 0.3
                  }} className="h-full flex flex-col" data-id="element-3196">
                        <div className="flex justify-between items-start mb-8" data-id="element-3197">
                          <div data-id="element-3198">
                            <Badge variant="warning" className="mb-3 bg-amber-500/20 text-amber-400 border-amber-500/30" data-id="element-3199">
                              Auditorías Anuales
                            </Badge>
                            <h3 className="text-3xl font-bold text-white mb-2" data-id="element-3200">
                              Estándares Globales
                            </h3>
                            <p className="text-gray-400" data-id="element-3201">
                              Nuestras instalaciones operan bajo las normativas
                              más estrictas de seguridad alimentaria humana y
                              animal.
                            </p>
                          </div>
                        </div>

                        <div className="grid gap-4 overflow-y-auto custom-scrollbar pr-2" data-id="element-3202">
                          {[{
                        name: 'IFS Food',
                        desc: 'International Featured Standards. Garantiza la seguridad y calidad alimentaria en los procesos de fabricación.',
                        icon: ShieldCheck,
                        color: 'text-blue-400',
                        bg: 'bg-blue-500/10',
                        border: 'border-blue-500/20'
                      }, {
                        name: 'ISO 22000',
                        desc: 'Sistema de gestión de inocuidad de los alimentos. Control total de riesgos en toda la cadena de suministro.',
                        icon: Factory,
                        color: 'text-purple-400',
                        bg: 'bg-purple-500/10',
                        border: 'border-purple-500/20'
                      }, {
                        name: 'FEDIAF Compliant',
                        desc: 'Cumplimiento estricto de las guías nutricionales de la Federación Europea de la Industria de Alimentos para Mascotas.',
                        icon: Activity,
                        color: 'text-emerald-400',
                        bg: 'bg-emerald-500/10',
                        border: 'border-emerald-500/20'
                      }, {
                        name: 'Certificado Ecológico',
                        desc: 'Acreditación oficial para nuestros productos de la línea BIO, garantizando ingredientes libres de pesticidas.',
                        icon: Leaf,
                        color: 'text-amber-400',
                        bg: 'bg-amber-500/10',
                        border: 'border-amber-500/20'
                      }].map((cert, i) => <div key={i} className={`flex gap-5 p-5 rounded-2xl border transition-all hover:bg-white/5 ${cert.bg} ${cert.border}`} data-id="element-3203">
                              <div className={`w-12 h-12 rounded-xl ${cert.bg} flex items-center justify-center shrink-0 border ${cert.border}`} data-id="element-3204">
                                <cert.icon className={`w-6 h-6 ${cert.color}`} data-id="element-3205" />
                              </div>
                              <div data-id="element-3206">
                                <div className="flex items-center gap-2 mb-1" data-id="element-3207">
                                  <h4 className="font-bold text-white text-lg" data-id="element-3208">
                                    {cert.name}
                                  </h4>
                                  <CheckCircle2 className={`w-4 h-4 ${cert.color}`} data-id="element-3209" />
                                </div>
                                <p className="text-gray-400 text-sm leading-relaxed" data-id="element-3210">
                                  {cert.desc}
                                </p>
                              </div>
                            </div>)}
                        </div>
                      </motion.div>}
                  </AnimatePresence>
                </div>
              </div>
            </EnhancedScrollReveal>
          </div>
        </div>
      </div>
    </section>;
}